import React, { useEffect, useState } from 'react'
import { withRouter } from 'react-router-dom'
import StatusMessage, { useStatusMessage } from '../../../../../ui/StatusMessage/StatusMessage'
import getDoc from '../../../../../utils/db/getDoc'
import getCollection from '../../../../../utils/db/getCollection'
import promiseAllValues from '../../../../../utils/promiseAllValues'
import { createForm } from '../../../../../utils/newforms/createForm'
import getFieldRenderObject from '../../../../../utils/newforms/render/getFieldRenderObject'
import styles from './OutletManger.module.scss'
import { isNull, isString } from 'lodash'
import ModulePanel from '../../../../../ui/ModulePanel/ModulePanel'
import FormRender from '../../../../../utils/newforms/render/FormRender'
import ReadOnlyDetailsCard from '../../../../../ui/ReadOnlyDetailsCard/ReadOnlyDetailsCard'
import Price from '../../ProductsList/Price'
import SpecificationsBlock from '../../ProductsList/ProductsManager/SpecificationsBlock/SpecificationsBlock'
import syncForm from '../../../../../utils/newforms/changed/syncForm'
import Button from '../../../../../ui/Button/Button'
import Spinner from '../../../../../ui/Spinner/Spinner'
import getOutletReasonLabel from '../../getOutletReasonLabel'
import isFormValid from '../../../../../utils/newforms/validation/isFormValid'
import getFormValues from '../../../../../utils/newforms/getFormValues'
import { updateDoc } from '../../../../../utils/db/updateDoc'
import { CATALOG_TAXONOMY_COLLECTION } from '../../CatalogTaxonomy/catalogTaxonomyConstants'
import {
  buildCatalogTaxonomyNodesById,
  formatProductTaxonomySummary,
} from '../../../productSchema/formatProductTaxonomySummary'
import {
  COMPACT_PRODUCT_SEO_FIELDS,
  CompactProductSeoForm,
  OUTLET_PRODUCTS_SEO_OG_STORAGE_PATH,
  OutletSeoSection,
  buildSeoDotPathUpdate,
  deleteSeoOgStorageFile,
  resolveOgImageForSave,
  seoFormDefaultsFromDoc,
} from '../../../seo'

function OutletManager({ ...router }) {
  const { productId } = router.match.params

  const [form, setForm] = useState(null)
  const [seoForm, setSeoForm] = useState(null)
  const [ogImage, setOgImage] = useState(null)
  const [previousOgImage, setPreviousOgImage] = useState(null)
  const [productData, setProductData] = useState(null)
  const [showErrors] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [statusMessage, setStatusMessage] = useStatusMessage()

  useEffect(() => {
    if (!productId) {
      setForm(null)
      setSeoForm(null)
      setOgImage(null)
      setPreviousOgImage(null)
      setProductData(null)
      return undefined
    }

    let cancelled = false
    setForm(null)
    setSeoForm(null)
    setOgImage(null)
    setPreviousOgImage(null)
    setProductData(null)

    getDoc({ path: 'outletProducts', docId: productId })
      .then((data) => {
        if (!data) return null

        return promiseAllValues({
          brandData: data.brandId
            ? getDoc({
                path: 'brands',
                docId: data.brandId,
                docIdName: 'id',
              })
            : null,
          premiumProduct: getDoc({
            path: 'products',
            docId: productId,
            docIdName: 'id',
          }),
          taxonomyNodes: getCollection({
            path: CATALOG_TAXONOMY_COLLECTION,
            docIdName: 'id',
          }),
          data,
        })
      })
      .then((res) => {
        if (cancelled || !res || !res.data) return

        const seoFormData = seoFormDefaultsFromDoc(res.data.seo, { fields: COMPACT_PRODUCT_SEO_FIELDS })
        const nodesById = buildCatalogTaxonomyNodesById(res.taxonomyNodes || [])
        const taxonomySummary = formatProductTaxonomySummary(res.premiumProduct || {}, nodesById)
        setProductData({
          ...res.data,
          brandName: res.brandData ? res.brandData.name : '-',
          premiumSlug: res.premiumProduct?.slug || null,
          taxonomySummary,
        })
        setForm(
          createForm({
            formPattern: new ProductForm(),
            formData: res.data,
          })
        )
        setSeoForm(
          createForm({
            formPattern: new CompactProductSeoForm(),
            formData: seoFormData,
          })
        )
        setOgImage(seoFormData.ogImage || null)
        setPreviousOgImage(seoFormData.ogImage || null)
      })

    return () => {
      cancelled = true
    }
  }, [productId])

  const getMinExpirationSpecPrice = (expirationRules = []) => {
    const prices = expirationRules
      .map((rule) => {
        const normalizedPrice = `${rule?.expPrice ?? ''}`.replace(/\s+/g, '').replace(',', '.')
        const price = Number(normalizedPrice)

        return Number.isFinite(price) ? price : null
      })
      .filter((price) => price !== null)

    return prices.length > 0 ? Math.min(...prices) : null
  }

  const onSubmit = async () => {
    if (!productId || !form || !seoForm) return

    if (!isFormValid({ form })) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return setStatusMessage({
        show: true,
        type: 'fail',
        message: 'Будь ласка, заповніть усі поля форми',
        closeAfter: 5000,
      })
    }

    setIsLoading(true)
    setStatusMessage({ show: false, type: '', message: '', closeAfter: null })

    let uploadedOrphan = null

    try {
      const data = getFormValues({ form })

      const values = {
        ...data,
        specifications: isString(data.specifications.descriptionLong)
          ? data.specifications
          : getFormValues({ form: data.specifications }),
        updated: new Date(),
      }

      if (data.outletReason === 'productExpiration') {
        const minExpirationSpecPrice = getMinExpirationSpecPrice(data.expirationRules)

        if (minExpirationSpecPrice !== null) {
          values.specPrice = minExpirationSpecPrice
        }
      }

      const outletResult = await updateDoc({ path: 'outletProducts', docId: productId, data: values })
      if (outletResult === 'fail') {
        throw new Error('outlet update failed')
      }

      const seoValues = getFormValues({ form: seoForm })
      const { nextOgImage, uploadedOrphan: orphan, previousToDelete } = await resolveOgImageForSave({
        docId: productId,
        localOgImage: ogImage,
        previousOgImage,
        storagePath: OUTLET_PRODUCTS_SEO_OG_STORAGE_PATH,
      })
      uploadedOrphan = orphan

      const seoPayload = buildSeoDotPathUpdate(
        {
          ...seoValues,
          ogImage: nextOgImage,
        },
        { fields: COMPACT_PRODUCT_SEO_FIELDS }
      )

      const seoResult = await updateDoc({
        path: 'outletProducts',
        docId: productId,
        data: seoPayload,
      })

      if (seoResult === 'fail') {
        throw new Error('seo update failed')
      }

      if (previousToDelete) {
        await deleteSeoOgStorageFile({
          docId: productId,
          media: previousToDelete,
          storagePath: OUTLET_PRODUCTS_SEO_OG_STORAGE_PATH,
        })
      }

      if (!nextOgImage && previousOgImage) {
        await deleteSeoOgStorageFile({
          docId: productId,
          media: previousOgImage,
          storagePath: OUTLET_PRODUCTS_SEO_OG_STORAGE_PATH,
        })
      }

      const refreshed = await getDoc({ path: 'outletProducts', docId: productId })
      const seoFormData = seoFormDefaultsFromDoc(refreshed?.seo, { fields: COMPACT_PRODUCT_SEO_FIELDS })
      setProductData((prev) => ({
        ...(refreshed || values),
        brandName: prev?.brandName,
        premiumSlug: prev?.premiumSlug,
        taxonomySummary: prev?.taxonomySummary,
      }))
      setForm(
        createForm({
          formPattern: new ProductForm(),
          formData: refreshed || values,
        })
      )
      setSeoForm(
        createForm({
          formPattern: new CompactProductSeoForm(),
          formData: seoFormData,
        })
      )
      setOgImage(seoFormData.ogImage || null)
      setPreviousOgImage(seoFormData.ogImage || null)

      setStatusMessage({
        show: true,
        type: 'success',
        message: 'Дані збережено',
        closeAfter: 5000,
      })
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (error) {
      console.log('🚀 ~ onSubmit ~ error:', error)
      if (uploadedOrphan) {
        await deleteSeoOgStorageFile({
          docId: productId,
          media: uploadedOrphan,
          storagePath: OUTLET_PRODUCTS_SEO_OG_STORAGE_PATH,
        })
      }
      setStatusMessage({
        show: true,
        type: 'fail',
        message: 'Не вдалося зберегти. Спробуйте ще раз.',
        closeAfter: 7000,
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={styles.root}>
      {!isNull(form) && !isNull(seoForm) && !isNull(productData) ? (
        <>
          {statusMessage.show && (
            <StatusMessage className="Site-StatusMessage" type={statusMessage.type} message={statusMessage.message} />
          )}
          <ModulePanel title={productData.name}>
            <FormRender
              sections={[
                {
                  fields: ['isActive'],
                },
              ]}
              form={form}
              setForm={setForm}
              errors={showErrors}
            />

            <ReadOnlyDetailsCard
              title="Основна інформація"
              rows={[
                { label: 'Код товару', value: productData.barcode || '—', mono: true },
                {
                  label: 'Slug',
                  value: productData.premiumSlug || '—',
                  mono: true,
                },
                { label: 'Бренд', value: productData.brandName || '—' },
                {
                  label: 'Основна категорія',
                  value: productData.taxonomySummary?.mainLine || 'Не вибрано',
                },
                {
                  label: 'Додаткові категорії',
                  value: productData.taxonomySummary?.extrasLines?.length
                    ? productData.taxonomySummary.extrasLines.join('; ')
                    : '—',
                },
                { label: 'Ціна', value: <Price num={productData.price} /> },
                {
                  label: 'Спеціальна ціна',
                  value: (
                    <span className={styles.specPriceValue}>
                      <Price num={productData.specPrice} />
                    </span>
                  ),
                },
                {
                  label: 'Причина Outlet',
                  value: (
                    <span className={styles.warningValue}>
                      {getOutletReasonLabel(productData.outletReason)}
                    </span>
                  ),
                },
              ]}
            />
            <div className="PriceInfo-Container">
              <FormRender
                sections={[
                  {
                    fields: ['expirationRules'],
                  },
                ]}
                form={form}
                setForm={setForm}
                errors={showErrors}
              />
            </div>
            <p className="Title">Додаткова інформація</p>
            <div className="AdditionalInfo-Container">
              <FormRender
                sections={[
                  {
                    fields: ['name', 'descriptionShort'],
                  },
                ]}
                form={form}
                setForm={setForm}
                errors={showErrors}
              />
            </div>
            <SpecificationsBlock
              formData={productData.specifications}
              syncState={(moduleState) =>
                syncForm({
                  propIdentifier: 'specifications',
                  form,
                  setForm,
                  newForm: moduleState,
                })
              }
            />

            <OutletSeoSection
              form={seoForm}
              setForm={setSeoForm}
              ogImage={ogImage}
              setOgImage={setOgImage}
            />
          </ModulePanel>
          <div className="Buttons">
            <Button
              title="Зберегти"
              theme="solid"
              fill="accent"
              iconPosition="right"
              size={32}
              isLoading={isLoading}
              onClick={onSubmit}
            />
          </div>
        </>
      ) : (
        <Spinner type="module" />
      )}
    </div>
  )
}

class ProductForm {
  constructor() {
    this.name = {
      field: {
        fieldId: 'name',
        fieldType: 'input',
        inputType: 'text',
        label: 'Назва товару',
        required: false,
      },
      render: getFieldRenderObject(),
    }
    this.descriptionShort = {
      field: {
        fieldId: 'descriptionShort',
        fieldType: 'textarea',
        label: 'Короткий опис товару',
        required: false,
      },
      render: getFieldRenderObject(),
    }
    this.expirationRules = {
      field: {
        fieldId: 'expirationRules',
        fieldType: 'fieldset',
        label: 'Ціни експіраційних товарів',
        fields: [
          {
            field: {
              fieldId: 'expirationDate',
              fieldType: 'date',
              label: 'Дата експірації',
              required: true,
            },
            render: getFieldRenderObject(),
          },
          {
            field: {
              fieldId: 'expPrice',
              fieldType: 'input',
              inputType: 'text',
              label: 'Ціна',
              required: true,
            },
            render: getFieldRenderObject(),
          },
          {
            field: {
              fieldId: 'quantity',
              fieldType: 'input',
              inputType: 'number',
              label: 'Кі-сть одиниць',
              required: true,
            },
            render: getFieldRenderObject(),
          },
        ],
        required: true,
      },
      render: getFieldRenderObject({ byDefault: false, ifFieldId: 'outletReason', value: 'productExpiration' }),
    }
    this.outletReason = {
      field: {
        fieldId: 'outletReason',
        fieldType: 'input',
        inputType: 'text',
        required: false,
      },
      render: getFieldRenderObject(),
    }

    this.specifications = {
      field: { fieldId: 'specifications', value: {} },
      render: getFieldRenderObject({ isSystem: true }),
    }

    this.photos = {
      field: { fieldId: 'photos', value: [] },
      render: getFieldRenderObject({ isSystem: true }),
    }

    this.updated = {
      field: { fieldId: 'created', value: new Date() },
      render: getFieldRenderObject({ isSystem: true }),
    }
    this.isActive = {
      field: {
        fieldId: 'isActive',
        fieldType: 'checkbox',
        label: 'Активний',
        value: true,
      },
      render: getFieldRenderObject(),
    }
  }
}

export default withRouter(OutletManager)
